import { useCallback } from 'react';
import { useUIStore } from '../../../shared/stores/useUIStore';
import { useConfirmationDialog } from '../../../shared/ui/dialogs/useConfirmationDialog';
import type { ConnectorParameterTarget } from '../../../shared/types';
import { buildCreateRequiredParametersConsent } from '../../../shared/ui/workflow/createRequiredParametersConsent';

const DEFAULT_PARAMETER_TARGET: ConnectorParameterTarget = {
  destination: 'project',
  bindingKind: 'type',
};

/**
 * Consent gate for the "Create required parameters" checkbox used by every
 * wizard step. Enabling it means required PipeEX parameters will be written
 * into the active Revit model on Continue, so the user must explicitly
 * acknowledge that first — checking the box opens a warning dialog and the
 * flag is set only on confirm. Unchecking applies immediately. Pages whose
 * consent must not leak into later steps can provide a local state setter.
 */
export const useCreateRequiredParametersConfirmation = (
  onConfirmedChange?: (confirmed: boolean) => void,
  target: ConnectorParameterTarget = DEFAULT_PARAMETER_TARGET
) => {
  const setDefaultPropertiesConfirmed = useUIStore((state) => state.setDefaultPropertiesConfirmed);
  const setConfirmed = onConfirmedChange ?? setDefaultPropertiesConfirmed;
  const { requestConfirmation, confirmationDialog } = useConfirmationDialog();

  const requestDefaultPropertiesChange = useCallback(
    async (checked: boolean): Promise<boolean> => {
      if (!checked) {
        setConfirmed(false);
        return true;
      }

      const confirmed = await requestConfirmation(buildCreateRequiredParametersConsent(target));

      if (confirmed) {
        setConfirmed(true);
      }

      return confirmed;
    },
    [requestConfirmation, setConfirmed, target]
  );

  return { requestDefaultPropertiesChange, confirmationDialog };
};
